import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  DroneCredentialsRevokedEvent,
  GeofenceBoundaryEvent,
  GeofenceBoundaryKind,
  LowBatteryLandingEvent,
} from '../../types/events';

/**
 * Fleet safety alerts (#452).
 *
 * A geofence boundary event is a report, not a request: by the time it reaches
 * the UI the drone has already turned back. Nothing here can change what
 * happened, so the slice exists purely to make sure an operator sees it.
 */

/** A boundary event, held until an operator dismisses it. */
export interface GeofenceAlert {
  /** Stable key for React lists and dismissal. */
  id: string;
  droneId: string;
  geofenceId: string;
  kind: GeofenceBoundaryKind;
  latitude: number;
  longitude: number;
  /** The place, and so the distance, is from the drone's last GPS fix (#842). */
  positionIsLastFixed: boolean;
  /** Metres between the drone and the boundary when it acted. */
  distanceM: number;
  /** What the drone did, e.g. "return_to_base". */
  action: string;
  timestamp: string;
}

/**
 * A drone landing itself on a low battery, held until an operator dismisses it
 * (#629).
 *
 * Kept alongside the boundary alerts because it is the same kind of fact: the
 * aircraft has already acted, nothing here can change it, and the only thing this
 * slice exists to do is make sure somebody sees it. A drone that put itself down
 * in a field while nobody had the tab open is exactly what must not happen
 * quietly.
 */
export interface BatteryLandingAlert {
  id: string;
  droneId: string;
  /** Absent when the drone does not know where it is (#751). */
  latitude?: number;
  longitude?: number;
  /** The position is the last GPS fix, not where the drone is now. */
  positionIsLastFixed: boolean;
  /** Absent when the autopilot measures no charge (#751). */
  batteryPercent?: number;
  voltsPerCell?: number;
  thresholdVoltsPerCell?: number;
  /** True when a remote pilot has the aircraft, so our automation stood down. */
  remotePilotInCommand: boolean;
  timestamp: string;
}

/**
 * A drone whose tokens were revoked because its refresh token was used twice
 * (#777, C8), held until the pilot dismisses it. The drone is cut off and
 * cannot say so itself, so this is the only place the pilot hears of it.
 */
export interface CredentialsRevokedAlert {
  id: string;
  droneId: string;
  reason: string;
  revokedAt: string;
}

interface SafetyState {
  /** Newest first. */
  geofenceAlerts: GeofenceAlert[];
  /** Newest first. */
  batteryLandingAlerts: BatteryLandingAlert[];
  /** Newest first, one per drone. */
  credentialsRevokedAlerts: CredentialsRevokedAlert[];
}

/**
 * A drone bouncing along its boundary, or a fleet of them doing it at once,
 * must not grow this without bound. Older alerts fall off the end; the log on
 * the backend remains the full record.
 */
export const MAX_GEOFENCE_ALERTS = 20;

/** The same bound, for the same reason, on automatic-landing announcements. */
export const MAX_BATTERY_LANDING_ALERTS = 20;

const initialState: SafetyState = {
  geofenceAlerts: [],
  batteryLandingAlerts: [],
  credentialsRevokedAlerts: [],
};

const safetySlice = createSlice({
  name: 'safety',
  initialState,
  reducers: {
    /**
     * Record a boundary event from the real-time gateway.
     *
     * A drone still at its boundary keeps one alert rather than stacking a new
     * one on every report: it is the same unresolved situation, and burying the
     * operator in duplicates is how the important one gets missed.
     */
    geofenceBoundaryReported: (
      state,
      action: PayloadAction<GeofenceBoundaryEvent['data']>
    ) => {
      const event = action.payload;
      const alert: GeofenceAlert = {
        id: `${event.drone_id}:${event.geofence_id}:${event.timestamp}`,
        droneId: event.drone_id,
        geofenceId: event.geofence_id,
        kind: event.event,
        latitude: event.latitude,
        longitude: event.longitude,
        positionIsLastFixed: event.position_is_last_fixed ?? false,
        distanceM: event.distance_m,
        action: event.action,
        timestamp: event.timestamp,
      };

      const existing = state.geofenceAlerts.findIndex(
        (a) => a.droneId === alert.droneId && a.geofenceId === alert.geofenceId
      );
      if (existing !== -1) {
        state.geofenceAlerts.splice(existing, 1);
      }

      state.geofenceAlerts.unshift(alert);
      state.geofenceAlerts = state.geofenceAlerts.slice(0, MAX_GEOFENCE_ALERTS);
    },

    /** Dismiss one alert the operator has seen. */
    geofenceAlertDismissed: (state, action: PayloadAction<string>) => {
      state.geofenceAlerts = state.geofenceAlerts.filter(
        (a) => a.id !== action.payload
      );
    },

    /** Dismiss everything currently showing. */
    geofenceAlertsCleared: (state) => {
      state.geofenceAlerts = [];
    },

    /**
     * Record an automatic landing from the real-time gateway (#629).
     *
     * One alert per drone per descent, keyed on the drone rather than the
     * timestamp: an aircraft on a failing pack may announce more than once, and
     * the second announcement is the same unresolved situation. Replacing keeps
     * the freshest position, which is the field an operator is reading.
     */
    lowBatteryLandingReported: (
      state,
      action: PayloadAction<LowBatteryLandingEvent['data']>
    ) => {
      const event = action.payload;
      const alert: BatteryLandingAlert = {
        id: `battery-landing:${event.drone_id}`,
        droneId: event.drone_id,
        latitude: event.latitude,
        longitude: event.longitude,
        positionIsLastFixed: event.position_is_last_fixed ?? false,
        batteryPercent: event.battery_percent,
        voltsPerCell: event.volts_per_cell,
        thresholdVoltsPerCell: event.threshold_volts_per_cell,
        remotePilotInCommand: event.remote_pilot_in_command ?? false,
        timestamp: event.timestamp,
      };

      const existing = state.batteryLandingAlerts.findIndex((a) => a.id === alert.id);
      if (existing !== -1) {
        state.batteryLandingAlerts[existing] = alert;
        return;
      }
      state.batteryLandingAlerts.unshift(alert);
      state.batteryLandingAlerts = state.batteryLandingAlerts.slice(
        0,
        MAX_BATTERY_LANDING_ALERTS
      );
    },

    batteryLandingAlertDismissed: (state, action: PayloadAction<string>) => {
      state.batteryLandingAlerts = state.batteryLandingAlerts.filter(
        (a) => a.id !== action.payload
      );
    },

    /**
     * Record a drone cut off for a reused refresh token (#777, C8). One alert
     * per drone: a second revocation is the same unresolved situation.
     */
    droneCredentialsRevokedReported: (
      state,
      action: PayloadAction<DroneCredentialsRevokedEvent['data']>
    ) => {
      const event = action.payload;
      const alert: CredentialsRevokedAlert = {
        id: `credentials-revoked:${event.drone_id}`,
        droneId: event.drone_id,
        reason: event.reason,
        revokedAt: event.revoked_at,
      };
      state.credentialsRevokedAlerts = [
        alert,
        ...state.credentialsRevokedAlerts.filter((a) => a.id !== alert.id),
      ];
    },

    credentialsRevokedAlertDismissed: (state, action: PayloadAction<string>) => {
      state.credentialsRevokedAlerts = state.credentialsRevokedAlerts.filter(
        (a) => a.id !== action.payload
      );
    },
  },
});

export const {
  geofenceBoundaryReported,
  lowBatteryLandingReported,
  batteryLandingAlertDismissed,
  droneCredentialsRevokedReported,
  credentialsRevokedAlertDismissed,
  geofenceAlertDismissed,
  geofenceAlertsCleared,
} = safetySlice.actions;

export default safetySlice.reducer;
